import type { IronPythonDiagnostic } from './ironPythonSyntax';

const PythonKeywords = new Set([
  'and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def', 'del',
  'elif', 'else', 'except', 'False', 'finally', 'for', 'from', 'global', 'if', 'import',
  'in', 'is', 'lambda', 'None', 'nonlocal', 'not', 'or', 'pass', 'raise', 'return',
  'True', 'try', 'while', 'with', 'yield',
]);

const PythonBuiltins = new Set([
  'ArithmeticError', 'AssertionError', 'AttributeError', 'BaseException', 'BufferError',
  'BytesWarning', 'DeprecationWarning', 'EOFError', 'EnvironmentError', 'Exception',
  'FloatingPointError', 'FutureWarning', 'GeneratorExit', 'IOError', 'ImportError',
  'ImportWarning', 'IndentationError', 'IndexError', 'KeyError', 'KeyboardInterrupt',
  'LookupError', 'MemoryError', 'NameError', 'NotImplemented', 'NotImplementedError',
  'OSError', 'OverflowError', 'PendingDeprecationWarning', 'ReferenceError', 'RuntimeError',
  'RuntimeWarning', 'StandardError', 'StopIteration', 'SyntaxError', 'SyntaxWarning',
  'SystemError', 'SystemExit', 'TabError', 'TypeError', 'UnboundLocalError', 'UnicodeError',
  'UnicodeWarning', 'UserWarning', 'ValueError', 'Warning', 'ZeroDivisionError',
  '__builtins__', '__debug__', '__doc__', '__import__', '__name__', '__package__',
  'abs', 'all', 'any', 'apply', 'basestring', 'bin', 'bool', 'buffer', 'bytearray',
  'bytes', 'callable', 'chr', 'classmethod', 'cmp', 'coerce', 'compile', 'complex',
  'delattr', 'dict', 'dir', 'divmod', 'enumerate', 'eval', 'execfile', 'file', 'filter',
  'float', 'format', 'frozenset', 'getattr', 'globals', 'hasattr', 'hash', 'help', 'hex',
  'id', 'input', 'int', 'intern', 'isinstance', 'issubclass', 'iter', 'len', 'list',
  'locals', 'long', 'map', 'max', 'memoryview', 'min', 'next', 'object', 'oct', 'open',
  'ord', 'pow', 'print', 'property', 'range', 'raw_input', 'reduce', 'reload', 'repr',
  'reversed', 'round', 'set', 'setattr', 'slice', 'sorted', 'staticmethod', 'str', 'sum',
  'super', 'tuple', 'type', 'unichr', 'unicode', 'vars', 'xrange', 'zip',
]);

interface AssignmentState {
  line: number;
  wasRead: boolean;
}

interface FunctionRegion {
  firstLineIndex: number;
  lastLineIndex: number;
  indentation: number;
  parameters: string[];
}

interface MaskState {
  tripleQuote: "'''" | '"""' | null;
}

const identifierPattern = /\b[A-Za-z_]\w*\b/g;

const indentationWidth = (line: string) => line.match(/^[ \t]*/)?.[0].length ?? 0;

const maskStringsAndComments = (line: string, state: MaskState): string => {
  const characters = [...line];
  let quote: "'" | '"' | null = null;
  let escaped = false;

  for (let index = 0; index < characters.length; index += 1) {
    const triple = line.slice(index, index + 3);
    if (state.tripleQuote) {
      characters[index] = ' ';
      if (triple === state.tripleQuote) {
        characters[index + 1] = ' ';
        characters[index + 2] = ' ';
        index += 2;
        state.tripleQuote = null;
      }
      continue;
    }

    if (quote) {
      characters[index] = ' ';
      if (escaped) {
        escaped = false;
      } else if (line[index] === '\\') {
        escaped = true;
      } else if (line[index] === quote) {
        quote = null;
      }
      continue;
    }

    if (triple === "'''" || triple === '"""') {
      characters[index] = ' ';
      characters[index + 1] = ' ';
      characters[index + 2] = ' ';
      index += 2;
      state.tripleQuote = triple;
      continue;
    }

    if (characters[index] === "'" || characters[index] === '"') {
      quote = characters[index] as "'" | '"';
      characters[index] = ' ';
      continue;
    }

    if (characters[index] === '#') {
      characters.fill(' ', index);
      break;
    }
  }

  return characters.join('');
};

const collectMaskedLines = (lines: string[]): string[] => {
  const state: MaskState = { tripleQuote: null };
  return lines.map((line) => maskStringsAndComments(line, state));
};

const collectImportedNames = (lines: string[]): Set<string> => {
  const names = new Set<string>();
  lines.forEach((line) => {
    const fromImport = line.match(/^\s*from\s+[A-Za-z_.][\w.]*\s+import\s+(.+)$/);
    if (fromImport) {
      fromImport[1].split(',').forEach((part) => {
        const tokens = part.trim().split(/\s+as\s+/);
        const name = tokens[tokens.length - 1].trim();
        if (/^[A-Za-z_]\w*$/.test(name)) {
          names.add(name);
        }
      });
    }

    const directImport = line.match(/^\s*import\s+(.+)$/);
    if (directImport) {
      directImport[1].split(',').forEach((part) => {
        const tokens = part.trim().split(/\s+as\s+/);
        const name = tokens.length > 1 ? tokens[1] : tokens[0].split('.')[0];
        if (/^[A-Za-z_]\w*$/.test(name)) {
          names.add(name);
        }
      });
    }
  });
  return names;
};

const findEntryPointRegion = (lines: string[]): FunctionRegion | null => {
  const firstLineIndex = lines.findIndex((line) => /^\s*def\s+get_elem_property\s*\(/.test(line));
  if (firstLineIndex < 0) {
    return null;
  }

  const definition = lines[firstLineIndex];
  const indentation = indentationWidth(definition);
  const parameterSource = definition.match(/\(([^)]*)\)/)?.[1] ?? '';
  const parameters = parameterSource
    .split(',')
    .map((parameter) => parameter.trim().split(/[:=]/)[0].trim())
    .filter((parameter) => /^[A-Za-z_]\w*$/.test(parameter));
  let lastLineIndex = lines.length - 1;

  for (let index = firstLineIndex + 1; index < lines.length; index += 1) {
    if (lines[index].trim() && indentationWidth(lines[index]) <= indentation) {
      lastLineIndex = index - 1;
      break;
    }
  }

  return { firstLineIndex, lastLineIndex, indentation, parameters };
};

const isComprehension = (line: string) => /[[({].*\bfor\s+[A-Za-z_]/.test(line);

const assignmentTarget = (line: string): string | null => {
  const match = line.match(/^\s*([A-Za-z_]\w*)\s*(?::[^=]+)?=(?!=)/);
  return match?.[1] ?? null;
};

const definitionTargets = (line: string): Set<string> => {
  const targets = new Set<string>();
  const forTarget = line.match(/^\s*for\s+([A-Za-z_]\w*)\s+in\b/);
  const exceptTarget = line.match(/^\s*except\b[^:]*\bas\s+([A-Za-z_]\w*)\s*:/);
  const withTarget = line.match(/^\s*with\b.*\bas\s+([A-Za-z_]\w*)\s*:/);
  [forTarget?.[1], exceptTarget?.[1], withTarget?.[1]].forEach((target) => {
    if (target) {
      targets.add(target);
    }
  });
  return targets;
};

const shouldIgnoreIdentifier = (line: string, index: number, identifier: string): boolean => {
  if (PythonKeywords.has(identifier)) {
    return true;
  }

  const before = line.slice(0, index).trimEnd();
  const after = line.slice(index + identifier.length);
  return before.endsWith('.') || /^\s*=(?!=)/.test(after);
};

const collectReads = (line: string, ignored: Set<string>): Array<{ name: string; column: number }> => {
  const reads: Array<{ name: string; column: number }> = [];
  for (const match of line.matchAll(identifierPattern)) {
    const identifier = match[0];
    const index = match.index ?? 0;
    if (!ignored.has(identifier) && !shouldIgnoreIdentifier(line, index, identifier)) {
      reads.push({ name: identifier, column: index + 1 });
    }
  }
  return reads;
};

const addWarning = (
  diagnostics: IronPythonDiagnostic[],
  line: number,
  message: string,
  column?: number
) => {
  if (!diagnostics.some((diagnostic) => diagnostic.line === line && diagnostic.message === message)) {
    diagnostics.push({ line, column, message, severity: 'warning' });
  }
};

export const analyzeIronPythonSemantics = (
  sourceLines: string[],
  diagnostics: IronPythonDiagnostic[]
) => {
  const maskedLines = collectMaskedLines(sourceLines);
  const region = findEntryPointRegion(maskedLines);
  if (!region) {
    return;
  }

  const defined = new Set([...PythonBuiltins, ...collectImportedNames(maskedLines), ...region.parameters]);
  const assignments = new Map<string, AssignmentState>();

  for (let index = region.firstLineIndex + 1; index <= region.lastLineIndex; index += 1) {
    const line = maskedLines[index];
    const lineNumber = index + 1;
    if (!line.trim()) {
      continue;
    }

    if (/^\s*except\s*:\s*$/.test(line)) {
      addWarning(
        diagnostics,
        lineNumber,
        'Avoid a bare except: because it hides IronPython failures.',
        line.search(/\S/) + 1
      );
    }

    if (isComprehension(line)) {
      continue;
    }

    const target = assignmentTarget(line);
    const targets = definitionTargets(line);
    if (target) {
      targets.add(target);
    }

    const ignored = new Set(targets);
    const reads = collectReads(line, ignored);
    reads.forEach(({ name, column }) => {
      const assignment = assignments.get(name);
      if (assignment) {
        assignment.wasRead = true;
      }
      if (!defined.has(name)) {
        addWarning(diagnostics, lineNumber, `'${name}' is not defined in this script.`, column);
      }
    });

    targets.forEach((name) => {
      const previousAssignment = assignments.get(name);
      if (target === name && previousAssignment && !previousAssignment.wasRead) {
        addWarning(
          diagnostics,
          lineNumber,
          `'${name}' is overwritten before the value assigned on line ${previousAssignment.line} is read.`,
          Math.max(1, line.indexOf(name) + 1)
        );
      }
      defined.add(name);
      if (target === name) {
        assignments.set(name, { line: lineNumber, wasRead: false });
      }
    });
  }
};
